<script context="module">
  export const EMOJI_SIZE = 28;
</script>

<script lang="ts">
  import { clickOutside } from "app/actions/clickOutside";
  import { portal } from "app/actions/portal.svelte";
  import { createPopperActions } from "svelte-popperjs";
  import { quintOut } from "svelte/easing";
  import { fade } from "svelte/transition";
  import { createEventDispatcher } from "svelte";
  import EmojiPickerPreview from "./EmojiPickerPreview.svelte";
  import EmojiPickerFull from "./EmojiPickerFull.svelte";
  import type { Placement } from "tippy.js";

  export let viewMode: "closed" | "preview" | "full" = "closed";

  let dispatch = createEventDispatcher();
  let placement: Placement | undefined = "top-start";

  const DELAY_SHOW_PREVIEW_MS = 180;
  const DELAY_HIDE_PREVIEW_MS = 900;

  let timer: string | number | NodeJS.Timeout | undefined;

  // we use the createPopperActions function to create the popperRef and popperContent variables.
  // the popperRef represents the target element.
  // the popperContent represents the element that will be positioned relative to the target element.
  $: [popperRef, popperContent, getInstance] = createPopperActions({
    placement: "top-start",
    strategy: "absolute",
  });

  $: {
    // we use a setTimeout to get the placement value after the popper has been positioned
    // the viewMode is used as a trigger to get the placement value
    viewMode;
    setTimeout(() => {
      placement = getInstance()?.state.placement;
    }, 0);
  }

  const extraOpts = {
    modifiers: [
      {
        name: "offset",
        options: { offset: [0, 4] },
      },
      {
        name: "preventOverflow",
        options: {
          altAxis: true, // allow the picker to be positioned on the opposite axis if there is not enough space on the current axis
          padding: 10, // add padding to the picker to prevent it from being positioned too close to the edge of the screen
        },
      },
    ],
  };
</script>

<button
  class="ov-focus relative after:rounded"
  type="button"
  use:popperRef
  on:click|stopPropagation|preventDefault={async () => {
    clearTimeout(timer);
    viewMode = "full";
  }}
  on:mouseover={() => {
    clearTimeout(timer);
    timer = setTimeout(() => (viewMode = "preview"), DELAY_SHOW_PREVIEW_MS);
  }}
  on:mouseleave={() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // we close the picker if the user leaves the button only if the picker is in preview mode
      if (viewMode === "preview") viewMode = "closed";
    }, DELAY_HIDE_PREVIEW_MS);
  }}
  on:focus={() => {
    clearTimeout(timer);
    timer = setTimeout(() => (viewMode = "preview"), DELAY_SHOW_PREVIEW_MS);
  }}
  on:blur={() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // we close the picker if the user leaves the button only if the picker is in preview mode
      if (viewMode === "preview") viewMode = "closed";
    }, DELAY_HIDE_PREVIEW_MS);
  }}
>
  <slot />
</button>

<!-- preview mode emoji picker -->
{#if viewMode === "preview"}
  <div
    class="overflow-hidden rounded-lg border-[0.5px] border-black/20 glossy-effect shadow-md dark:border-white/20"
    in:fade={{ duration: 100, easing: quintOut }}
    use:portal={{ target: "body", hasBackLayer: false }}
    use:popperContent={extraOpts}
    on:mouseover={() => {
      clearTimeout(timer);
    }}
    on:mouseleave={() => {
      clearTimeout(timer);
      timer = setTimeout(() => (viewMode = "closed"), DELAY_HIDE_PREVIEW_MS);
    }}
    on:focus={() => {
      clearTimeout(timer);
    }}
    on:blur={() => {
      clearTimeout(timer);
      timer = setTimeout(() => (viewMode = "closed"), DELAY_HIDE_PREVIEW_MS);
    }}
    role="tooltip"
  >
    <EmojiPickerPreview
      on:selection={(emoji) => {
        dispatch("selection", emoji.detail);
        viewMode = "closed";
      }}
      on:full-mode={() => {
        clearTimeout(timer);
        viewMode = "full";
      }}
    />
  </div>

  <!-- full mode emoji picker -->
{:else if viewMode === "full"}
  <div
    in:fade={{ duration: 70, easing: quintOut }}
    out:fade={{ duration: 100, easing: quintOut }}
    use:portal={{ target: "body", hasBackLayer: true }}
    use:popperContent={extraOpts}
    use:clickOutside={() => (viewMode = "closed")}
  >
    <EmojiPickerFull
      on:selection={(emoji) => {
        viewMode = "closed";
        dispatch("selection", emoji.detail);
      }}
      {placement}
    />
  </div>
{/if}

<!-- close emoji picker on Escape -->
<svelte:window
  on:keydown={(e) => {
    if (viewMode === "closed") return;

    if (e.key === "Escape") {
      viewMode = "closed";
      e.preventDefault();
    }
  }}
/>
